import { useState, useEffect } from 'react';
import { SearchBar } from '@/components/home/search-bar';
import { ContentGrid } from '@/components/home/content-grid';
import { QuoteCard } from '@/components/home/quote-card';
import { KeywordFilters } from '@/components/home/keyword-filters';
import {
  EditContentDialog,
  ContentItemDeprecated,
} from '@/components/admin/edit-content-dialog';
import {
  QuoteEditDialog,
  QuoteItem,
} from '@/components/admin/quote-edit-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useProfile } from '@/lib/providers';
import { AdminRole, Content, ContentConfig, ContentWithConfig } from '@/types/custom';
import { QUOTE_OF_THE_DAY } from '@/lib/hardcoded-data';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { useContentList } from '@/lib/hooks/use-content';
import AudioCard from '@/components/audio-card';
import { getDeepLink, CONTENT_TYPES } from '@/lib/constants';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Trash2, Star } from 'lucide-react';
import { ContentType } from '@/types/custom';
import Loading from '@/components/loading';

type ScopeType = 'global' | 'sector' | 'organization' | 'team' | 'user';

interface ScopeEntity {
  id: string;
  name: string;
}

interface FeaturedItem {
  id: string;
  content_id: string;
  scope_type: string;
  scope_id: string | null;
  sort_order: number;
}

export default function AdminHome() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { toast } = useToast();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] =
    useState<ContentItemDeprecated | null>(null);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<QuoteItem | null>(null);
  const isAdmin = profile?.role === AdminRole;

  // View As state
  const [scopeType, setScopeType] = useState<ScopeType>('global');
  const [scopeId, setScopeId] = useState<string | null>(null);

  // Entity lists for cascading dropdowns
  const [sectors, setSectors] = useState<ScopeEntity[]>([]);
  const [organizations, setOrganizations] = useState<ScopeEntity[]>([]);
  const [teams, setTeams] = useState<ScopeEntity[]>([]);
  const [users, setUsers] = useState<ScopeEntity[]>([]);

  // Featured content state
  const [featuredItems, setFeaturedItems] = useState<FeaturedItem[]>([]);
  const [featuredContent, setFeaturedContent] = useState<ContentWithConfig[]>([]);
  const [isLoadingFeatured, setIsLoadingFeatured] = useState(false);

  // Add content dialog
  const [addFeaturedOpen, setAddFeaturedOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<ContentType | 'all'>('all');
  const { contentWithConfigs: allContent, loading: loadingAllContent } =
    useContentList('*');

  // Fetch entity lists on mount
  useEffect(() => {
    const fetchEntities = async () => {
      const [sectorRes, orgRes, teamRes, userRes] = await Promise.all([
        supabase.from('sector').select('id, name').order('name'),
        supabase.from('organization').select('id, name').order('name'),
        supabase.from('team').select('id, name').order('name'),
        supabase.from('profiles').select('id, naam').order('naam'),
      ]);

      if (sectorRes.data) setSectors(sectorRes.data.map((s) => ({ id: s.id, name: s.name })));
      if (orgRes.data) setOrganizations(orgRes.data.map((o) => ({ id: o.id, name: o.name })));
      if (teamRes.data) setTeams(teamRes.data.map((t) => ({ id: t.id, name: t.name })));
      if (userRes.data)
        setUsers(
          userRes.data
            .filter((u) => u.naam)
            .map((u) => ({ id: u.id, name: u.naam! })),
        );
    };

    fetchEntities();
  }, []);

  // Reset scopeId when scopeType changes
  useEffect(() => {
    setScopeId(null);
  }, [scopeType]);

  // Fetch featured content when scope changes
  useEffect(() => {
    fetchFeaturedContent();
  }, [scopeType, scopeId]);

  const fetchFeaturedContent = async () => {
    // For non-global scopes, require a scopeId to be selected
    if (scopeType !== 'global' && !scopeId) {
      setFeaturedItems([]);
      setFeaturedContent([]);
      return;
    }

    setIsLoadingFeatured(true);
    try {
      let query = supabase
        .from('homepage_featured_content')
        .select('*')
        .eq('scope_type', scopeType)
        .order('sort_order');

      if (scopeType === 'global') {
        query = query.is('scope_id', null);
      } else {
        query = query.eq('scope_id', scopeId!);
      }

      const { data, error } = await query;
      if (error) throw error;

      setFeaturedItems(data ?? []);

      // Fetch the actual content for these featured items
      if (data && data.length > 0) {
        const contentIds = data.map((f) => f.content_id);
        const { data: contentData, error: contentError } = await supabase
          .from('content')
          .select('*, content_configs(*), keywords(*)')
          .in('id', contentIds);

        if (contentError) throw contentError;

        const mapped: ContentWithConfig[] = (contentData ?? []).map((item) => ({
          content: {
            id: item.id,
            title: item.title,
            description: item.description,
            body: item.body,
            image_url: item.image_url,
            content_type: item.content_type,
            published: item.published,
            created_at: item.created_at,
            updated_at: item.updated_at,
          } as Content,
          configs: (item.content_configs || []) as ContentConfig[],
          keywords: item.keywords ?? [],
        }));

        // Sort by the featured items order
        const orderMap = new Map(data.map((f) => [f.content_id, f.sort_order]));
        mapped.sort((a, b) => (orderMap.get(a.content.id) ?? 0) - (orderMap.get(b.content.id) ?? 0));

        setFeaturedContent(mapped);
      } else {
        setFeaturedContent([]);
      }
    } catch (error) {
      console.error('Error fetching featured content:', error);
    } finally {
      setIsLoadingFeatured(false);
    }
  };

  const handleAddFeatured = async (contentId: string) => {
    try {
      const { error } = await supabase.from('homepage_featured_content').insert({
        content_id: contentId,
        scope_type: scopeType,
        scope_id: scopeType === 'global' ? null : scopeId,
        sort_order: featuredItems.length,
      });

      if (error) throw error;

      setAddFeaturedOpen(false);
      fetchFeaturedContent();
      toast({ title: 'Succes', description: 'Content uitgelicht!' });
    } catch (error) {
      console.error('Error adding featured content:', error);
      toast({
        title: 'Fout',
        description: 'Kon content niet uitlichten.',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveFeatured = async (featuredId: string) => {
    try {
      const { error } = await supabase
        .from('homepage_featured_content')
        .delete()
        .eq('id', featuredId);

      if (error) throw error;

      fetchFeaturedContent();
      toast({ title: 'Succes', description: 'Uitgelichte content verwijderd.' });
    } catch (error) {
      console.error('Error removing featured content:', error);
      toast({
        title: 'Fout',
        description: 'Kon uitgelichte content niet verwijderen.',
        variant: 'destructive',
      });
    }
  };

  const handleAddContent = () => {
    setSelectedContent(null);
    setEditDialogOpen(true);
  };

  const handleAddQuote = () => {
    setSelectedQuote(null);
    setQuoteDialogOpen(true);
  };

  const handleSaveQuote = (updatedQuote: QuoteItem) => {
    toast({
      title: 'Quote updated',
      description: 'De quote is succesvol bijgewerkt.',
    });
  };

  const getEntityList = (): ScopeEntity[] => {
    switch (scopeType) {
      case 'sector':
        return sectors;
      case 'organization':
        return organizations;
      case 'team':
        return teams;
      case 'user':
        return users;
      default:
        return [];
    }
  };

  const getScopeLabel = (): string => {
    switch (scopeType) {
      case 'global':
        return 'Iedereen';
      case 'sector':
        return 'Sector';
      case 'organization':
        return 'Organisatie';
      case 'team':
        return 'Team';
      case 'user':
        return 'Gebruiker';
      default:
        return '';
    }
  };

  // Content already featured (to filter from the add dialog)
  const featuredContentIds = new Set(featuredItems.map((f) => f.content_id));

  const availableContent = allContent
    .filter((item) => item.content.published)
    .filter((item) => !featuredContentIds.has(item.content.id));

  return (
    <div className="space-y-8">
      {/* View As Section */}
      <section className="bg-card border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Star className="h-5 w-5 text-yellow-500" />
          <h2 className="text-lg font-semibold">Bekijken als</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Selecteer een doelgroep om uitgelichte content te bekijken en beheren.
        </p>

        <div className="flex flex-wrap gap-3 items-end">
          <div className="w-48">
            <label className="text-sm font-medium mb-1 block">Doelgroep</label>
            <Select
              value={scopeType}
              onValueChange={(val) => setScopeType(val as ScopeType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="global">Iedereen</SelectItem>
                <SelectItem value="sector">Sector</SelectItem>
                <SelectItem value="organization">Organisatie</SelectItem>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="user">Gebruiker</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scopeType !== 'global' && (
            <div className="w-64">
              <label className="text-sm font-medium mb-1 block">
                {getScopeLabel()}
              </label>
              <Select
                value={scopeId ?? ''}
                onValueChange={(val) => setScopeId(val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={`Selecteer ${getScopeLabel().toLowerCase()}...`} />
                </SelectTrigger>
                <SelectContent>
                  {getEntityList().map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Featured Content for selected scope */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-muted-foreground">
              Uitgelichte content voor {getScopeLabel().toLowerCase()}
              {scopeId && scopeType !== 'global' && (
                <span className="font-normal">
                  {' '}
                  &middot;{' '}
                  {getEntityList().find((e) => e.id === scopeId)?.name}
                </span>
              )}
            </h3>
            <Button
              size="sm"
              onClick={() => setAddFeaturedOpen(true)}
              disabled={scopeType !== 'global' && !scopeId}
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Toevoegen
            </Button>
          </div>

          {isLoadingFeatured ? (
            <Loading />
          ) : featuredContent.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
              Geen uitgelichte content voor deze doelgroep.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredContent.map((item) => {
                const featuredItem = featuredItems.find(
                  (f) => f.content_id === item.content.id,
                );
                return (
                  <div key={item.content.id} className="relative group">
                    <AudioCard
                      content={item.content}
                      showLabel
                      onClick={() =>
                        navigate(
                          getDeepLink(
                            item.content.content_type,
                            item.content.id,
                          ),
                        )
                      }
                    />
                    <button
                      onClick={() =>
                        featuredItem && handleRemoveFeatured(featuredItem.id)
                      }
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Verwijderen uit uitgelicht"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Add Featured Content Dialog */}
      <Dialog
        open={addFeaturedOpen}
        onOpenChange={(open) => {
          setAddFeaturedOpen(open);
          if (!open) setCategoryFilter('all');
        }}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Content uitlichten</DialogTitle>
            <DialogDescription>
              Kies content om uit te lichten voor{' '}
              {getScopeLabel().toLowerCase()}
              {scopeId && scopeType !== 'global'
                ? ` "${getEntityList().find((e) => e.id === scopeId)?.name}"`
                : ''}
              .
            </DialogDescription>
          </DialogHeader>

          {/* Category filter tabs */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={categoryFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setCategoryFilter('all')}
            >
              Alles
            </Button>
            {(Object.entries(CONTENT_TYPES) as [ContentType, string][]).map(
              ([type, label]) => {
                const count = availableContent.filter(
                  (item) => item.content.content_type === type,
                ).length;
                if (count === 0) return null;
                return (
                  <Button
                    key={type}
                    size="sm"
                    variant={categoryFilter === type ? 'default' : 'outline'}
                    onClick={() => setCategoryFilter(type)}
                  >
                    {label} ({count})
                  </Button>
                );
              },
            )}
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {loadingAllContent ? (
              <Loading />
            ) : availableContent.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Alle gepubliceerde content is al uitgelicht voor deze
                doelgroep.
              </p>
            ) : categoryFilter === 'all' ? (
              // Grouped by category
              <div className="space-y-6 p-1">
                {(Object.entries(CONTENT_TYPES) as [ContentType, string][]).map(
                  ([type, label]) => {
                    const items = availableContent.filter(
                      (item) => item.content.content_type === type,
                    );
                    if (items.length === 0) return null;
                    return (
                      <div key={type}>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                          {label}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {items.map((item) => (
                            <div
                              key={item.content.id}
                              className="cursor-pointer hover:ring-2 hover:ring-green-500 rounded-lg transition-all"
                              onClick={() => handleAddFeatured(item.content.id)}
                            >
                              <AudioCard
                                content={item.content}
                                showLabel
                                onClick={() =>
                                  handleAddFeatured(item.content.id)
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            ) : (
              // Filtered by single category
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-1">
                {availableContent
                  .filter(
                    (item) => item.content.content_type === categoryFilter,
                  )
                  .map((item) => (
                    <div
                      key={item.content.id}
                      className="cursor-pointer hover:ring-2 hover:ring-green-500 rounded-lg transition-all"
                      onClick={() => handleAddFeatured(item.content.id)}
                    >
                      <AudioCard
                        content={item.content}
                        showLabel
                        onClick={() => handleAddFeatured(item.content.id)}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Existing sections */}
      <section>
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2>
              <span className="text-2xl font-semibold">
                Welkom{' '}
                <span className="font-bold">
                  {profile?.naam?.split(' ')[0] || 'Admin'}
                </span>
                , wat heb je nu nodig?
              </span>
            </h2>
            <div className="flex gap-3">
              <button
                onClick={handleAddQuote}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
                aria-label="Nieuwe quote toevoegen"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Nieuwe Quote
              </button>
              <button
                onClick={handleAddContent}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
                aria-label="Nieuwe content toevoegen"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Nieuwe Content
              </button>
            </div>
          </div>
          <div className="mb-4">
            <SearchBar />
          </div>
          <KeywordFilters />
        </div>
        <div className="mb-6">
          <h2 className="text-xl text-muted-foreground font-bold mb-4">
            Nieuwste Content
          </h2>
        </div>
        <ContentGrid articlesOnly={false} />
      </section>

      <section>
        <h2 className="text-xl text-muted-foreground font-bold mb-4">
          Quote van de Dag
        </h2>
        <QuoteCard
          isAdmin={isAdmin}
          onEdit={() => {
            setSelectedQuote(QUOTE_OF_THE_DAY);
            setQuoteDialogOpen(true);
          }}
        />
      </section>

      <EditContentDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        contentItem={selectedContent}
        onSave={() => {}}
      />

      <QuoteEditDialog
        open={quoteDialogOpen}
        onOpenChange={setQuoteDialogOpen}
        quoteItem={selectedQuote}
        onSave={handleSaveQuote}
      />
    </div>
  );
}
